import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PenSquare, Save, Loader2 } from "lucide-react";
import { emailSignatureGet, emailSignatureSave } from "@/lib/api";
import { toast } from "sonner";

/**
 * EmailSignatureCard — dedicated card so business owners can find and
 * edit their outgoing-email identity without hunting through nested
 * settings. Used on estimates, invoices, and any future outbound flow.
 */
export default function EmailSignatureCard() {
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    emailSignatureGet().then(setSig).catch(() => setSig(null));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      setSig(await emailSignatureSave(sig));
      toast.success("Signature saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!sig) return null;

  return (
    <Card className="border border-slate-200 bg-white p-5" data-testid="signature-card">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
        <PenSquare className="h-4 w-4" /> Email signature
      </div>
      <h3 className="mt-1 font-heading text-lg font-bold text-slate-900">
        Sign every outgoing email with your name
      </h3>
      <p className="mt-1 text-sm text-slate-600">
        Applied to every estimate and invoice email so customers see who
        they're talking to. Change it any time — no re-send needed.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-xs font-semibold text-slate-500">From name</label>
          <input type="text" value={sig.from_name}
            onChange={(e) => setSig({ ...sig, from_name: e.target.value })}
            placeholder="e.g. Jane Doe · Owner"
            className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            data-testid="sig-card-from-name" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Reply-to email</label>
          <input type="email" value={sig.reply_to}
            onChange={(e) => setSig({ ...sig, reply_to: e.target.value })}
            placeholder="you@yourbusiness.com"
            className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            data-testid="sig-card-reply-to" />
        </div>
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-500">Signature block</label>
        <textarea rows={3} value={sig.signature}
          onChange={(e) => setSig({ ...sig, signature: e.target.value })}
          placeholder={"Jane Doe\nOwner, Demo Roofing\n(555) 010-2020"}
          className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          data-testid="sig-card-body" />
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm"
          className="bg-orange-600 text-white hover:bg-orange-700"
          onClick={save} disabled={busy}
          data-testid="sig-card-save">
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
          Save signature
        </Button>
      </div>
    </Card>
  );
}
